import styled from '@emotion/styled'

import {
    breakpoint,
    breakpointMax,
    calcRem,
    breakpointBetween,
} from 'component-library/__styling/style-functions'
import { metrics, colors } from 'component-library/__styling/settings'
import { themedValue } from 'component-library/__styling/themed-value'
import { conditionalStyles } from 'component-library/__styling/conditional-styles'
import { horizontalDividers, verticalDividers } from 'component-library/__styling/style-mixins'
import { ThemeMargins } from 'component-library/__styling/settings/metrics'

interface GridProps {
    initialColumns: number
    finalColumns: number
    verticalSpacing: keyof ThemeMargins | undefined
    stretchSelf?: boolean
}

export const StyledLima = styled('div')<GridProps>(({ stretchSelf, verticalSpacing, theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    flexGrow: 1,
    position: 'relative',
    width: '100%',
    height: stretchSelf ? '100%' : undefined,
    margin: '0 auto',
    marginBottom: verticalSpacing && calcRem(theme.margins[verticalSpacing]),
    paddingBottom: themedValue(theme, {
        thewest: metrics.thewest.margins.sm,
        fallback: undefined,
    }),
    borderBottom: themedValue(theme, {
        thewest: `4px double ${colors.thewest.greyGorilla}`,
        fallback: undefined,
    }),
}))
StyledLima.displayName = 'StyledLima'

export const StyledHeader = styled('div')(props => ({
    display: 'block',
    marginBottom: calcRem(props.theme.margins.md),
}))
StyledHeader.displayName = 'StyledHeader'

export interface CellProps {
    numberOfItems: number
    initialColumns: number
    intermediateColumns: number
    finalColumns: number
}

const getLastRowOfCards = (numberOfItems: number, columns: number) => {
    const calcItems = (numberOfItems / columns) % 1
    const lastRowOfCards = calcItems > 0 ? calcItems * columns : columns

    return lastRowOfCards
}

const getInitialWidth = (initialColumns: number) => {
    const cellWidth = 100 / initialColumns
    return cellWidth
}

const getWidth = (columns: number) => {
    const cellWidth = 100 / columns
    return cellWidth
}

export const StyledGridItem = styled('div')<CellProps>(props => {
    const { theme, initialColumns, intermediateColumns, numberOfItems, finalColumns } = props
    const themedMargin = themedValue(theme, {
        thewest: props.theme.margins.gridGap,
        sevennews: metrics.sevennews.margins.md,
        perthnow: metrics.perthnow.margins.md,
    })

    return {
        display: 'flex',
        justifyContent: 'stretch',
        flexDirection: 'column',
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: initialColumns > 1 ? `auto` : `100%`,
        width: `calc(${getInitialWidth(initialColumns) + '%'} - 19px)`,
        marginRight: initialColumns > 1 ? calcRem(themedMargin) : 0,
        marginBottom: calcRem(themedMargin),

        '& > *:not(:last-child)': {
            marginBottom: calcRem(themedMargin),
        },

        [':nth-child(' + initialColumns + 'n)']: {
            marginRight: '0',
        },

        // Removes top margin from top row
        [':nth-child(-n+' + initialColumns + ')']: { marginTop: 0 },

        // Removes bottom margin from bottom row
        [':nth-last-child(-n+' + initialColumns + ')']: { marginBottom: 0 },

        // Dividers for The West
        ...conditionalStyles(theme.kind === 'thewest', {
            position: 'relative',

            // Horizontal dividers for nested cards at all breakpoints
            '& > *:not(:last-child)::before': {
                ...horizontalDividers,
            },

            // Adds horizontal dividers for first level cards on small breakpoints only
            [breakpointMax('sm')]: {
                '&:not(:last-child)::before': {
                    ...horizontalDividers,
                },
            },
        }),

        [breakpoint('sm')]: {
            flexBasis: `auto`,
            marginRight: calcRem(themedMargin),
            width: `calc(${getWidth(intermediateColumns) + '%'} - 19px)`,

            [':nth-child(' + initialColumns + 'n)']: {
                marginRight: calcRem(themedMargin),
            },

            // Reset
            [':nth-child(' + intermediateColumns + 'n)']: { marginRight: 0 },
            [':last-child']: {
                marginRight: 0,
            },

            // Remove Top margin from top row
            [':nth-child(-n+' + intermediateColumns + ')']: { marginTop: 0 },

            // Remove bottom margin from bottom row
            // prettier-ignore
            [':nth-last-child(-n+' + getLastRowOfCards(numberOfItems, intermediateColumns) + ')']: {
                marginBottom: 0,
            },

            ...conditionalStyles(theme.kind === 'thewest', {
                '&:not(:first-child)::after': {
                    ...verticalDividers,
                },
            }),
        },

        ...conditionalStyles(theme.kind === 'thewest', {
            [breakpointBetween('sm', 'md')]: {
                // Add horizontal dividers on the bottom of the first row of cards for intermediate columns lengths - don't add it if there is only one row.
                // prettier-ignore
                [':not(:nth-last-child(-n+' + getLastRowOfCards(numberOfItems, intermediateColumns) +'))::before']: {
                    ...horizontalDividers,
                },

                // Remove Dividers for The West - Resets so they dont appear above "Medium breakpoint"
                [':nth-child(' + intermediateColumns + 'n + 1)::after']: {
                    content: 'none',
                },
            },
        }),

        [breakpoint('md')]: {
            width: `calc(${getWidth(finalColumns) + '%'} - ${themedMargin + 1 + 'px'})`,

            [':nth-child(' + initialColumns + 'n)']: {
                marginRight: calcRem(themedMargin),
            },

            // Reset
            [':nth-child(' + finalColumns + 'n)']: { marginRight: 0 },
            [':last-child']: {
                marginRight: 0,
            },

            // Remove Top margin from top row
            [':nth-child(-n+' + finalColumns + ')']: {
                marginTop: 0,
            },

            // Remove bottom margin from bottom row
            // prettier-ignore
            [':nth-last-child(-n+' + getLastRowOfCards(numberOfItems, finalColumns) + ')']: {
                marginBottom: 0,
            },

            ...conditionalStyles(theme.kind === 'thewest', {
                // Remove Dividers for The West
                // prettier-ignore
                ['&:nth-child(' + finalColumns + 'n + 1)::after']: {
                    content: 'none',
                },

                // Horizontal dividers if there is more than one row
                // prettier-ignore
                [':not(:nth-last-child(-n+' + getLastRowOfCards(numberOfItems, finalColumns) +'))::before']: {
                    ...horizontalDividers,
                }
            }),
        },
    }
})
